/**
 * ⌘K: go anywhere, find anything, and act on the selected or focused issues.
 * Pages ("status", "priority", ...) are opened by keyboard shortcuts too.
 */
import { UserAvatar } from "@/components/avatar";
import { ColorDot, PriorityIcon, ProjectStatusIcon, StateIcon, TeamIcon } from "@/components/icons";
import { Command, CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandShortcut } from "@/components/ui/command";
import { currentCycle, useMeId } from "@/lib/hooks";
import { ESTIMATES, PRIORITIES, identifier } from "@/lib/format";
import { CATEGORY_LABEL } from "@/lib/grouping";
import { shortcutLabel } from "@/lib/hotkeys";
import { addTo, archive, removeFrom, update, updateMany, create } from "@/lib/mutations";
import { useTable } from "@/lib/store";
import type { Issue } from "@/lib/types";
import { commandStore, liveSessionStore, openCreateIssue, useCommand, useLiveSession, type CommandPage } from "@/lib/ui";
import { useNavigate } from "@tanstack/react-router";
import {
  Archive,
  ArrowRight,
  BellOff,
  BellRing,
  BookOpen,
  Box,
  CalendarDays,
  ChartColumn,
  Check,
  CircleUserRound,
  Copy,
  FilePen,
  GitBranch,
  Hexagon,
  House,
  Inbox,
  Lightbulb,
  Link2,
  Map as MapIcon,
  Plus,
  Radio,
  RefreshCcw,
  Search,
  Settings,
  StickyNote,
  Tag,
  Target,
  Triangle,
  UserRound,
  Users,
} from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { toast } from "sonner";

function close() {
  commandStore.set((s) => ({ ...s, open: false }));
}

function setPage(page: CommandPage) {
  commandStore.set((s) => ({ ...s, page }));
}

function slug(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
}

async function copy(text: string, what: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${what} copied`);
  } catch {
    toast.error("Couldn't reach the clipboard.");
  }
}

interface Item {
  id: string;
  label: string;
  icon?: ReactNode;
  hint?: ReactNode;
  keywords?: string;
  onSelect: () => void;
  checked?: boolean;
}

function matchScore(q: string, text: string): number {
  if (!q) return 1;
  const t = text.toLowerCase();
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.every((w) => t.includes(w))) return 0;
  return t.startsWith(words[0]) ? 2 : 1;
}

export function CommandMenu() {
  const state = useCommand();
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const me = useMeId();
  const live = useLiveSession();
  const issues = useTable("issue");
  const teams = useTable("team");
  const states = useTable("workflowState");
  const users = useTable("user");
  const labels = useTable("label");
  const projects = useTable("project");
  const cycles = useTable("cycle");
  const docs = useTable("document");
  const ideas = useTable("idea");
  const initiatives = useTable("initiative");
  const customers = useTable("customer");
  const views = useTable("view");

  const targets = state.issueIds.map((id) => issues[id]).filter(Boolean) as Issue[];
  const teamIds = [...new Set(targets.map((i) => i.teamId))];
  const oneTeam = teamIds.length === 1 ? teamIds[0] : undefined;
  const ids = targets.map((i) => i.id);
  const set = (data: Partial<Issue>) => {
    if (ids.length === 1) update("issue", ids[0], data);
    else updateMany("issue", ids, data);
    close();
  };

  const groups = useMemo((): { heading: string; items: Item[] }[] => {
    const q = query.trim();
    const filter = (items: Item[]) => items.map((it) => ({ it, s: matchScore(q, `${it.label} ${it.keywords ?? ""}`) })).filter((x) => x.s > 0).sort((a, b) => b.s - a.s).map((x) => x.it);

    if (state.page === "status") {
      if (oneTeam) {
        return [{ heading: "Change status", items: filter(Object.values(states).filter((s) => s.teamId === oneTeam && !s.archivedAt).sort((a, b) => a.position - b.position).map((s) => ({ id: s.id, label: s.name, icon: <StateIcon category={s.category} color={s.color} />, checked: targets.every((i) => i.stateId === s.id), onSelect: () => set({ stateId: s.id }) }))) }];
      }
      const cats = ["backlog", "unstarted", "started", "completed", "canceled"] as const;
      return [
        {
          heading: "Change status",
          items: filter(
            cats.map((c) => ({
              id: c,
              label: CATEGORY_LABEL[c],
              onSelect: () => {
                for (const i of targets) {
                  const st = Object.values(states).filter((s) => s.teamId === i.teamId && s.category === c && !s.archivedAt).sort((a, b) => a.position - b.position)[0];
                  if (st) update("issue", i.id, { stateId: st.id });
                }
                close();
              },
            })),
          ),
        },
      ];
    }
    if (state.page === "priority") {
      return [{ heading: "Set priority", items: filter([1, 2, 3, 4, 0].map((p) => ({ id: String(p), label: PRIORITIES.find((x) => x.value === p)!.label, icon: <PriorityIcon priority={p} />, checked: targets.every((i) => i.priority === p), onSelect: () => set({ priority: p }) }))) }];
    }
    if (state.page === "assignee") {
      const people = Object.values(users).filter((u) => u.active).sort((a, b) => (a.id === me ? -1 : b.id === me ? 1 : a.name.localeCompare(b.name)));
      return [
        {
          heading: "Assign to",
          items: filter([
            { id: "none", label: "No assignee", icon: <UserAvatar userId={null} />, onSelect: () => set({ assigneeId: null }) },
            ...people.map((u) => ({ id: u.id, label: u.id === me ? `${u.name} (you)` : u.name, keywords: u.email, icon: <UserAvatar userId={u.id} />, checked: targets.every((i) => i.assigneeId === u.id), onSelect: () => set({ assigneeId: u.id }) })),
          ]),
        },
      ];
    }
    if (state.page === "labels") {
      const usable = Object.values(labels).filter((l) => !l.isGroup && !l.archivedAt && (!l.teamId || l.teamId === oneTeam));
      return [
        {
          heading: "Labels (choose several)",
          items: filter(
            usable.map((l) => {
              const all = targets.every((i) => i.labelIds.includes(l.id));
              return {
                id: l.id,
                label: l.parentId ? `${labels[l.parentId]?.name}: ${l.name}` : l.name,
                icon: <ColorDot color={l.color} />,
                checked: all,
                onSelect: () => {
                  for (const i of targets) {
                    if (all) removeFrom("issue", i.id, "labelIds", [l.id]);
                    else {
                      const siblings = l.parentId ? i.labelIds.filter((x) => labels[x]?.parentId === l.parentId && x !== l.id) : [];
                      if (siblings.length) update("issue", i.id, { labelIds: [...i.labelIds.filter((x) => !siblings.includes(x)), l.id].sort() });
                      else addTo("issue", i.id, "labelIds", [l.id]);
                    }
                  }
                },
              };
            }),
          ),
        },
      ];
    }
    if (state.page === "project") {
      const list = Object.values(projects).filter((p) => !p.archivedAt && p.status !== "completed" && p.status !== "canceled");
      return [
        {
          heading: "Move to project",
          items: filter([
            { id: "none", label: "No project", icon: <Box className="size-4" />, onSelect: () => set({ projectId: null, milestoneId: null }) },
            ...list.map((p) => ({ id: p.id, label: p.name, icon: <ProjectStatusIcon status={p.status} />, checked: targets.every((i) => i.projectId === p.id), onSelect: () => set({ projectId: p.id, milestoneId: null }) })),
          ]),
        },
      ];
    }
    if (state.page === "cycle") {
      if (!oneTeam) return [{ heading: "Cycles belong to one team", items: [] }];
      const today = new Date().toISOString().slice(0, 10);
      const list = Object.values(cycles).filter((c) => c.teamId === oneTeam && !c.archivedAt && c.endsOn > today).sort((a, b) => a.startsOn.localeCompare(b.startsOn));
      const cur = currentCycle(list);
      return [
        {
          heading: "Move to cycle",
          items: filter([
            { id: "none", label: "No cycle", icon: <RefreshCcw className="size-4" />, onSelect: () => set({ cycleId: null }) },
            ...list.map((c) => ({ id: c.id, label: c.name || `Cycle ${c.number}`, hint: c.id === cur?.id ? "Current" : c.startsOn, icon: <RefreshCcw className="size-4" />, onSelect: () => set({ cycleId: c.id }) })),
          ]),
        },
      ];
    }
    if (state.page === "estimate") {
      const scale = oneTeam ? teams[oneTeam]?.estimateScale ?? "fibonacci" : "fibonacci";
      return [{ heading: "Estimate", items: filter([{ id: "none", label: "No estimate", onSelect: () => set({ estimate: null }) }, ...ESTIMATES[scale].map((e) => ({ id: String(e.value), label: e.label, icon: <Triangle className="size-4" />, onSelect: () => set({ estimate: e.value }) }))]) }];
    }
    if (state.page === "dueDate") {
      const d = (days: number) => {
        const x = new Date();
        x.setDate(x.getDate() + days);
        return x.toISOString().slice(0, 10);
      };
      const nextMonday = (() => {
        const x = new Date();
        x.setDate(x.getDate() + ((8 - x.getDay()) % 7 || 7));
        return x.toISOString().slice(0, 10);
      })();
      return [
        {
          heading: "Due date",
          items: filter([
            { id: "today", label: "Today", onSelect: () => set({ dueDate: d(0) }) },
            { id: "tomorrow", label: "Tomorrow", onSelect: () => set({ dueDate: d(1) }) },
            { id: "monday", label: "Next Monday", onSelect: () => set({ dueDate: nextMonday }) },
            { id: "week", label: "In a week", onSelect: () => set({ dueDate: d(7) }) },
            { id: "two", label: "In two weeks", onSelect: () => set({ dueDate: d(14) }) },
            { id: "clear", label: "No due date", onSelect: () => set({ dueDate: null }) },
          ]).map((x) => ({ ...x, icon: <CalendarDays className="size-4" /> })),
        },
      ];
    }
    if (state.page === "team") {
      return [{ heading: "Move to team", items: filter(Object.values(teams).filter((t) => !t.archivedAt).map((t) => ({ id: t.id, label: t.name, hint: t.key, icon: <TeamIcon teamId={t.id} />, onSelect: () => set({ teamId: t.id }) }))) }];
    }

    // Root page.
    const out: { heading: string; items: Item[] }[] = [];
    if (targets.length) {
      const one = targets.length === 1 ? targets[0] : null;
      const ident = one ? identifier(one, teams) : "";
      const subscribed = one ? one.subscriberIds.includes(me ?? "") : false;
      out.push({
        heading: one ? `${ident} · ${one.title}` : `${targets.length} issues`,
        items: filter(
          [
            { id: "status", label: "Change status…", icon: <StateIcon category="started" color="currentColor" />, hint: <CommandShortcut>S</CommandShortcut>, onSelect: () => setPage("status") },
            { id: "priority", label: "Set priority…", icon: <PriorityIcon priority={2} />, hint: <CommandShortcut>P</CommandShortcut>, onSelect: () => setPage("priority") },
            { id: "assignee", label: "Assign to…", icon: <UserRound className="size-4" />, hint: <CommandShortcut>A</CommandShortcut>, onSelect: () => setPage("assignee") },
            { id: "assign-me", label: "Assign to me", icon: <UserAvatar userId={me} />, hint: <CommandShortcut>I</CommandShortcut>, onSelect: () => set({ assigneeId: me }) },
            { id: "labels", label: "Labels…", icon: <Tag className="size-4" />, hint: <CommandShortcut>L</CommandShortcut>, onSelect: () => setPage("labels") },
            { id: "project", label: "Move to project…", icon: <Hexagon className="size-4" />, hint: <CommandShortcut>⇧P</CommandShortcut>, onSelect: () => setPage("project") },
            { id: "cycle", label: "Move to cycle…", icon: <RefreshCcw className="size-4" />, hint: <CommandShortcut>⇧C</CommandShortcut>, onSelect: () => setPage("cycle") },
            { id: "estimate", label: "Set estimate…", icon: <Triangle className="size-4" />, hint: <CommandShortcut>E</CommandShortcut>, onSelect: () => setPage("estimate") },
            { id: "due", label: "Set due date…", icon: <CalendarDays className="size-4" />, hint: <CommandShortcut>⇧D</CommandShortcut>, onSelect: () => setPage("dueDate") },
            { id: "team", label: "Move to team…", icon: <Users className="size-4" />, onSelect: () => setPage("team") },
            ...(one
              ? [
                  { id: "open", label: "Open issue", icon: <ArrowRight className="size-4" />, onSelect: () => (navigate({ to: "/issue/$ident", params: { ident } }), close()) },
                  { id: "link", label: "Copy link", icon: <Link2 className="size-4" />, onSelect: () => (copy(`${location.origin}/issue/${ident}`, "Link"), close()) },
                  { id: "copy-id", label: "Copy ID", icon: <Copy className="size-4" />, onSelect: () => (copy(ident, "ID"), close()) },
                  { id: "branch", label: "Copy git branch name", icon: <GitBranch className="size-4" />, onSelect: () => (copy(`${(users[me ?? ""]?.name.split(" ")[0] ?? "me").toLowerCase()}/${ident.toLowerCase()}-${slug(one.title)}`, "Branch name"), close()) },
                  {
                    id: "subscribe",
                    label: subscribed ? "Unsubscribe from updates" : "Subscribe to updates",
                    icon: subscribed ? <BellOff className="size-4" /> : <BellRing className="size-4" />,
                    onSelect: () => (subscribed ? removeFrom("issue", one.id, "subscriberIds", [me!]) : addTo("issue", one.id, "subscriberIds", [me!]), close()),
                  },
                  { id: "sub", label: "Add sub-issue", icon: <Plus className="size-4" />, onSelect: () => (openCreateIssue({ teamId: one.teamId, parentId: one.id, projectId: one.projectId, cycleId: one.cycleId }), close()) },
                ]
              : []),
            {
              id: "archive",
              label: targets.length > 1 ? `Archive ${targets.length} issues` : "Archive",
              icon: <Archive className="size-4" />,
              onSelect: () => {
                for (const id of ids) archive("issue", id);
                toast.success(targets.length > 1 ? `${targets.length} issues archived` : "Archived");
                close();
              },
            },
          ] as Item[],
        ),
      });
    }
    out.push({
      heading: "Create",
      items: filter([
        { id: "new-issue", label: "Create issue", icon: <Plus className="size-4" />, hint: <CommandShortcut>C</CommandShortcut>, onSelect: () => (close(), openCreateIssue()) },
        {
          id: "new-project",
          label: "Create project",
          icon: <Hexagon className="size-4" />,
          onSelect: () => {
            const { id } = create("project", { name: q || "Untitled project", leadId: me, memberIds: me ? [me] : [], sortKey: "a0" });
            navigate({ to: "/project/$id", params: { id } });
            close();
          },
        },
        {
          id: "new-idea",
          label: q ? `Add idea “${q}”` : "Add an idea",
          icon: <Lightbulb className="size-4" />,
          onSelect: () => {
            const { id } = create("idea", { title: q || "Untitled idea", ownerId: me });
            navigate({ to: "/idea/$id", params: { id } });
            close();
          },
        },
        {
          id: "new-doc",
          label: "Create document",
          icon: <BookOpen className="size-4" />,
          onSelect: () => {
            const { id } = create("document", { title: q || "Untitled", contentText: "" });
            navigate({ to: "/doc/$id", params: { id } });
            close();
          },
        },
        {
          id: "live",
          label: live.on ? "End live planning session" : "Start live planning session (show cursors)",
          icon: <Radio className="size-4" />,
          onSelect: () => (liveSessionStore.set((s) => ({ ...s, on: !s.on, following: null })), close()),
        },
      ]),
    });
    if (q.length >= 1) {
      const issueHits = Object.values(issues)
        .filter((i) => !i.archivedAt)
        .map((i) => ({ i, s: matchScore(q, `${identifier(i, teams)} ${i.title}`) + (identifier(i, teams).toLowerCase() === q.toLowerCase() ? 5 : 0) }))
        .filter((x) => x.s > 0)
        .sort((a, b) => b.s - a.s || b.i.updatedAt.localeCompare(a.i.updatedAt))
        .slice(0, 12);
      if (issueHits.length)
        out.push({
          heading: "Issues",
          items: issueHits.map(({ i }) => ({
            id: i.id,
            label: i.title,
            icon: <StateIcon category={states[i.stateId]?.category ?? "backlog"} color={states[i.stateId]?.color ?? "#999"} />,
            hint: <span className="tabular text-xs text-muted-foreground">{identifier(i, teams)}</span>,
            onSelect: () => (navigate({ to: "/issue/$ident", params: { ident: identifier(i, teams) } }), close()),
          })),
        });
      const others: Item[] = [
        ...Object.values(projects).filter((p) => !p.archivedAt).map((p) => ({ id: p.id, label: p.name, icon: <Hexagon className="size-4" style={{ color: p.color }} />, hint: "Project", onSelect: () => (navigate({ to: "/project/$id", params: { id: p.id } }), close()) })),
        ...Object.values(initiatives).filter((p) => !p.archivedAt).map((p) => ({ id: p.id, label: p.name, icon: <Target className="size-4" />, hint: "Initiative", onSelect: () => (navigate({ to: "/initiative/$id", params: { id: p.id } }), close()) })),
        ...Object.values(ideas).filter((p) => !p.archivedAt).map((p) => ({ id: p.id, label: p.title, icon: <Lightbulb className="size-4" />, hint: "Idea", onSelect: () => (navigate({ to: "/idea/$id", params: { id: p.id } }), close()) })),
        ...Object.values(docs).filter((p) => !p.archivedAt).map((p) => ({ id: p.id, label: p.title, icon: <BookOpen className="size-4" />, hint: "Document", onSelect: () => (navigate({ to: "/doc/$id", params: { id: p.id } }), close()) })),
        ...Object.values(customers).filter((p) => !p.archivedAt).map((p) => ({ id: p.id, label: p.name, icon: <Users className="size-4" />, hint: "Customer", onSelect: () => (navigate({ to: "/customer/$id", params: { id: p.id } }), close()) })),
        ...Object.values(views).filter((p) => !p.archivedAt).map((p) => ({ id: p.id, label: p.name, icon: <Search className="size-4" />, hint: "View", onSelect: () => (navigate({ to: "/view/$id", params: { id: p.id } }), close()) })),
      ];
      const hits = filter(others).slice(0, 10);
      if (hits.length) out.push({ heading: "Everything else", items: hits });
      out.push({ heading: "", items: [{ id: "search-all", label: `Search everything for “${q}”`, icon: <Search className="size-4" />, onSelect: () => (navigate({ to: "/search", search: { q } }), close()) }] });
    }
    const nav: Item[] = [
      { id: "go-home", label: "Go to Home", icon: <House className="size-4" />, hint: <CommandShortcut>G H</CommandShortcut>, onSelect: () => navigate({ to: "/" }) },
      { id: "go-inbox", label: "Go to Inbox", icon: <Inbox className="size-4" />, hint: <CommandShortcut>G I</CommandShortcut>, onSelect: () => navigate({ to: "/inbox" }) },
      { id: "go-mine", label: "Go to My work", icon: <CircleUserRound className="size-4" />, hint: <CommandShortcut>G M</CommandShortcut>, onSelect: () => navigate({ to: "/my-issues" }) },
      { id: "go-drafts", label: "Go to Drafts", icon: <FilePen className="size-4" />, onSelect: () => navigate({ to: "/drafts" }) },
      { id: "go-stickies", label: "Go to Stickies", icon: <StickyNote className="size-4" />, hint: <CommandShortcut>G T</CommandShortcut>, onSelect: () => navigate({ to: "/stickies" }) },
      { id: "go-projects", label: "Go to Projects", icon: <Hexagon className="size-4" />, hint: <CommandShortcut>G P</CommandShortcut>, onSelect: () => navigate({ to: "/projects" }) },
      { id: "go-roadmap", label: "Go to Roadmap", icon: <MapIcon className="size-4" />, hint: <CommandShortcut>G R</CommandShortcut>, onSelect: () => navigate({ to: "/roadmap" }) },
      { id: "go-ideas", label: "Go to Idea shelf", icon: <Lightbulb className="size-4" />, hint: <CommandShortcut>G D</CommandShortcut>, onSelect: () => navigate({ to: "/ideas" }) },
      { id: "go-insights", label: "Go to Insights", icon: <ChartColumn className="size-4" />, onSelect: () => navigate({ to: "/insights" }) },
      { id: "go-settings", label: "Go to Settings", icon: <Settings className="size-4" />, hint: <CommandShortcut>G S</CommandShortcut>, onSelect: () => navigate({ to: "/settings/$section", params: { section: "profile" } }) },
      ...Object.values(teams)
        .filter((t) => !t.archivedAt)
        .flatMap((t) => [
          { id: `team-${t.id}`, label: `${t.name} issues`, icon: <TeamIcon teamId={t.id} />, onSelect: () => navigate({ to: "/team/$key/issues", params: { key: t.key } }) },
          ...(t.cyclesEnabled ? [{ id: `cycles-${t.id}`, label: `${t.name} cycles`, icon: <RefreshCcw className="size-4" />, onSelect: () => navigate({ to: "/team/$key/cycles", params: { key: t.key } }) }] : []),
        ]),
    ].map((i) => ({ ...i, onSelect: () => (i.onSelect(), close()) }));
    const navHits = filter(nav);
    if (navHits.length) out.push({ heading: "Go to", items: q ? navHits.slice(0, 8) : navHits.slice(0, 7) });
    return out;
  }, [query, state.page, targets, oneTeam, issues, teams, states, users, labels, projects, cycles, docs, ideas, initiatives, customers, views, me, live.on, navigate]);

  return (
    <CommandDialog
      open={state.open}
      onOpenChange={(o) => {
        commandStore.set((s) => ({ ...s, open: o, page: o ? s.page : "root" }));
        if (!o) setQuery("");
      }}
      title="Command menu"
      description="Search, navigate, and act on issues"
      className="max-w-[640px] sm:max-w-[640px]"
    >
      <Command shouldFilter={false} className="rounded-none! max-sm:flex-1">
      <CommandInput
        value={query}
        onValueChange={setQuery}
        placeholder={state.page === "root" ? (targets.length ? "Type a command, or search…" : "Search or type a command…") : `Search…`}
        onKeyDown={(e) => {
          if (e.key === "Backspace" && !query && state.page !== "root") setPage("root");
        }}
      />
      <CommandList className="max-h-[60vh] max-sm:max-h-none max-sm:flex-1">
        <CommandEmpty>Nothing matches “{query}”.</CommandEmpty>
        {groups.map((g, gi) => (
          <CommandGroup key={`${g.heading}-${gi}`} heading={g.heading || undefined}>
            {g.items.map((it) => (
              <CommandItem key={it.id} value={it.id} onSelect={it.onSelect} className="gap-2">
                {it.icon && <span className="flex size-4 items-center justify-center text-muted-foreground">{it.icon}</span>}
                <span className="flex-1 truncate">{it.label}</span>
                {it.checked && <Check className="size-3.5 text-muted-foreground" />}
                {it.hint && <span className="text-[12px] text-muted-foreground">{it.hint}</span>}
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
      <div className="-mx-1 -mb-1 flex items-center justify-between border-t px-4 py-2 text-[11.5px] text-muted-foreground">
        <span>{state.page !== "root" ? "Backspace to go back" : `${shortcutLabel("mod+k")} anywhere`}</span>
        <span>{Object.keys(issues).length} issues on this device</span>
      </div>
      </Command>
    </CommandDialog>
  );
}
